import { useState, useEffect } from "react";
import { useNavigate } from "react-router";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../../../firebase";
import * as theme from "../../../theme";
import TruCard from "../../TruCard";
import ActiveScanItem from "../items/ActiveScanItem";
import { Box, Button } from "@mui/material";

export default function PastScansList({ currentUser, practitionerList, clientList }) {
  const [allScans, setAllScans] = useState([]);
  const [displayedScans, setDisplayedScans] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const scansPerPage = 20;
  const navigate = useNavigate();

  // Fetch past scans from Firestore
  useEffect(() => {
    const fetchPastScans = async () => {
      try {
        const pastScansCollection = collection(db, "pastScans");
        const pastScansSnapshot = await getDocs(pastScansCollection);
        let scans = [];

        // Merge scans from all year documents
        pastScansSnapshot.forEach((doc) => {
          const yearData = doc.data();
          if (yearData.scans && Array.isArray(yearData.scans)) {
            scans = [...scans, ...yearData.scans];
          }
        });

        // Filter by practitionerId for non-manager/admin users
        if (currentUser.role !== "manager" && currentUser.role !== "admin") {
          scans = scans.filter((scan) => scan.practitionerId === currentUser.uid);
        }

        // Sort by dateCompleted (descending)
        scans.sort((a, b) => {
          const dateA = a.dateCompleted.toDate();
          const dateB = b.dateCompleted.toDate();
          return dateB - dateA; // Most recent first
        });

        setAllScans(scans);
      } catch (err) {
        console.error("Error fetching past scans:", err.message);
      }
    };

    fetchPastScans();
  }, [currentUser]);

  // Update displayed scans based on current page
  useEffect(() => {
    const startIndex = (currentPage - 1) * scansPerPage;
    const endIndex = startIndex + scansPerPage;
    setDisplayedScans(allScans.slice(startIndex, endIndex));
  }, [allScans, currentPage]);

  // Pagination handlers
  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < Math.ceil(allScans.length / scansPerPage)) {
      setCurrentPage(currentPage + 1);
    }
  };

  // Calculate total pages
  const totalPages = Math.ceil(allScans.length / scansPerPage);

  return (
    <TruCard title={"Past Scans"}>
      <div style={theme.Container.listColumn}>
        {displayedScans.length > 0 ? (
          displayedScans.map((scan) => (
            <ActiveScanItem
              key={scan.scanId}
              scan={{
                ...scan,
                id: scan.scanId, // Map scanId to id for ActiveScanItem compatibility
              }}
              clientName={
                clientList.find((c) => c.id === scan.clientId)
                  ? `${clientList.find((c) => c.id === scan.clientId).firstName} ${
                      clientList.find((c) => c.id === scan.clientId).lastName
                    }`
                  : null
              }
              practitionerName={
                practitionerList.find((p) => p.id === scan.practitionerId)
                  ? `${practitionerList.find((p) => p.id === scan.practitionerId).firstName} ${
                      practitionerList.find((p) => p.id === scan.practitionerId).lastName
                    }`
                  : "Unknown Practitioner"
              }
              actionLink={() => navigate(`/clients/${scan.clientId}/scans/${scan.scanId}`)}
            />
          ))
        ) : (
          <div style={theme.SubHeadingText}>No past scans found.</div>
        )}
      </div>
      {allScans.length > 0 && (
        <Box sx={{ display: "flex", justifyContent: "center", marginTop: "20px", gap: "10px" }}>
          <Button
            variant="outlined"
            color="primary"
            onClick={handlePreviousPage}
            disabled={currentPage === 1}
          >
            Previous
          </Button>
          <Box sx={{ alignSelf: "center" }}>
            Page {currentPage} of {totalPages}
          </Box>
          <Button
            variant="outlined"
            color="primary"
            onClick={handleNextPage}
            disabled={currentPage === totalPages}
          >
            Next
          </Button>
        </Box>
      )}
    </TruCard>
  );
}